// Utility functions for authentication token handling
import {
  setAuthToken as setCookieAuthToken,
  getAuthToken as getCookieAuthToken,
  setAuthUser as setCookieAuthUser,
  getAuthUser as getCookieAuthUser,
  clearAllAuthCookies,
  updateLastActivity,
  getRememberMe as getCookieRememberMe
} from './cookieUtils';

/**
 * Get the current authentication token from storage
 * @returns {string|null} The token or null if not found
 */
export const getAuthToken = () => {
  return getCookieAuthToken();
};

/**
 * Get the current user from storage
 * @returns {object|null} The user object or null if not found
 */
export const getAuthUser = () => {
  const user = getCookieAuthUser();
  
  if (user) {
    console.log('📖 getAuthUser retrieved:', {
      client_id: user.client_id || "superAdmin",
      userId: user.id || user._id,
      username: user.username
    });
  }
  
  return user;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  clearAllAuthCookies();
};

/**
 * Get authorization header for API requests
 * @param {string} clientId - Optional client ID to include in headers
 * @param {Object} user - User object with role and client_id
 * @returns {object} Headers object with Authorization and X-Client-ID headers
 */
export const getAuthHeaders = (clientId = null, user = null) => {
  const token = getAuthToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  // Add client ID header if available
  try {
    // Use provided user data or get from cookies
    const userData = user || getAuthUser();
    if (userData) {
      let finalClientId = clientId;
      
      if (!finalClientId) {
        if (userData.role === 'super_admin') {
          // For super admin, we'll need to get client ID from a different source
          // For now, we'll use the user's client_id if available
          finalClientId = userData.client_id || null;
        } else {
          // Regular users use their assigned client_id from auth
          finalClientId = userData.client_id || null;
        }
      }
      
      if (finalClientId) {
        headers['X-Client-ID'] = finalClientId;
      }
    }
  } catch (error) {
    console.warn('Failed to get client ID for headers:', error);
  }
  
  return headers;
};

/**
 * Check if user has specific permission
 * @param {string} permission - The permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (permission) => {
  const user = getAuthUser();
  if (!user || !user.permissions) return false;
  
  return user.permissions.includes('*') || user.permissions.includes(permission);
};

/**
 * Check if user has any of the required permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has any of the permissions
 */
export const hasAnyPermission = (permissions) => {
  const user = getAuthUser();
  if (!user || !user.permissions) return false;
  
  return user.permissions.includes('*') || permissions.some(permission => user.permissions.includes(permission));
};

/**
 * Check if user has all of the required permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} True if user has all of the permissions
 */
export const hasAllPermissions = (permissions) => {
  const user = getAuthUser();
  if (!user || !user.permissions) return false;
  
  return user.permissions.includes('*') || permissions.every(permission => user.permissions.includes(permission));
};

/**
 * Get user role
 * @returns {string|null} The user's role or null if not found
 */
export const getUserRole = () => {
  const user = getAuthUser();
  return user ? user.role : null;
};

/**
 * Check if user is Admin
 * @returns {boolean} True if user is Admin
 */
export const isAdmin = () => {
  return getUserRole() === 'admin';
};

/**
 * Check if user is Super Admin
 * @returns {boolean} True if user is Super Admin
 */
export const isSuperAdmin = () => {
  return getUserRole() === 'super_admin';
};

/**
 * Check if user is HR Manager
 * @returns {boolean} True if user is HR Manager
 */
export const isHRManager = () => {
  return getUserRole() === 'hr_manager';
};

/**
 * Check if user is Recruiter
 * @returns {boolean} True if user is Recruiter
 */
export const isRecruiter = () => {
  return getUserRole() === 'recruiter';
};

/**
 * Check if user is Viewer
 * @returns {boolean} True if user is Viewer
 */
export const isViewer = () => {
  return getUserRole() === 'viewer';
};

/**
 * Get user display name
 * @returns {string} The user's display name
 */
export const getUserDisplayName = () => {
  const user = getAuthUser();
  if (!user) return '';
  
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  
  return user.username || user.email || 'Unknown User';
};

/**
 * Set authentication token
 * @param {string} token - The token to store
 * @param {boolean} rememberMe - Whether to store persistently
 */
export const setAuthToken = (token, rememberMe = false) => {
  setCookieAuthToken(token, rememberMe);
};

/**
 * Set authentication user
 * @param {object} user - The user object to store
 * @param {boolean} rememberMe - Whether to store persistently
 */
export const setAuthUser = (user, rememberMe = false) => {
  console.log('💾 setAuthUser called with:', {
    client_id: user.client_id || "superAdmin",
    userId: user.id || user._id,
    username: user.username,
    rememberMe
  });
  
  setCookieAuthUser(user, rememberMe);
};

/**
 * Update user activity timestamp
 */
export const updateActivity = () => {
  updateLastActivity();
};

/**
 * Clear authentication data (alias for clearAuthData)
 */
export const clearAuth = () => {
  clearAuthData();
};

/**
 * Get remember me preference
 * @returns {boolean} True if remember me is enabled
 */
export const getRememberMePreference = () => {
  return getCookieRememberMe();
};




